import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import LeadCaptureModal from '../../../components/feature/LeadCaptureModal';
import { featuresData, relatedFeatures } from '../../../mocks/features';

export default function FuncionalidadeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const feature = slug ? featuresData[slug as keyof typeof featuresData] : null;

  useEffect(() => {
    if (!feature) return;

    const pageTitle = `${feature.title} - TG WEB | Sistema para Transportadoras`;
    const pageDescription = `${feature.description} Conheça os recursos do módulo de ${feature.title} do TG WEB: ${feature.features.slice(0, 3).join(', ')} e muito mais.`;
    const pageKeywords = `${feature.title}, TG WEB, sistema para transportadoras, ${feature.benefits.map(b => b.title.toLowerCase()).join(', ')}`;
    const canonicalUrl = `${window.location.origin}/funcionalidades/${feature.slug}`;

    document.title = pageTitle;

    const setMeta = (name: string, content: string, attr: string = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageDescription);
    setMeta('keywords', pageKeywords);

    setMeta('og:title', pageTitle, 'property');
    setMeta('og:description', pageDescription, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', feature.heroImage, 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDescription);
    setMeta('twitter:image', feature.heroImage);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let lastModified = document.querySelector('meta[http-equiv="Last-Modified"]') as HTMLMetaElement | null;
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('http-equiv', 'Last-Modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toUTCString());

    return () => {
      document.title = 'TG WEB - Sistema para Transportadoras';
      const tagsToRemove = [
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[property="og:url"]',
        'meta[property="og:type"]',
        'meta[property="og:image"]',
        'meta[name="twitter:card"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'meta[name="twitter:image"]',
        'meta[http-equiv="Last-Modified"]',
      ];
      tagsToRemove.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) el.remove();
      });
    };
  }, [feature]);

  useEffect(() => {
    if (!feature) return;

    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          'name': `TG WEB - ${feature.title}`,
          'description': feature.description,
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'Web',
          'url': `${window.location.origin}/funcionalidades/${feature.slug}`,
          'image': feature.heroImage,
          'inLanguage': 'pt-BR',
          'offers': {
            '@type': 'Offer',
            'availability': 'https://schema.org/InStock',
            'priceCurrency': 'BRL',
            'url': `${window.location.origin}/planos`,
          },
          'featureList': feature.features.join(', '),
          'screenshot': feature.screenshots.map((s) => ({
            '@type': 'ImageObject',
            'url': s.image,
            'caption': s.caption,
          })),
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': '4.8',
            'reviewCount': '150',
            'bestRating': '5',
            'worstRating': '1',
          },
        },
        {
          '@type': 'FAQPage',
          'mainEntity': feature.faq.map((item) => ({
            '@type': 'Question',
            'name': item.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': item.answer,
            },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Início',
              'item': window.location.origin,
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Funcionalidades',
              'item': `${window.location.origin}/funcionalidades`,
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': feature.title,
              'item': `${window.location.origin}/funcionalidades/${feature.slug}`,
            },
          ],
        },
      ],
    };

    const existingScript = document.querySelector('script[data-schema="feature-detail"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'feature-detail');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-schema="feature-detail"]');
      if (el) el.remove();
    };
  }, [feature]);

  if (!feature) {
    return <Navigate to="/funcionalidades" replace />;
  }

  const related = relatedFeatures[slug as keyof typeof relatedFeatures] || [];
  const relatedItems = related.map((s) => featuresData[s as keyof typeof featuresData]).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm">
            <Link to="/" className="text-neutral-500 hover:text-primary transition-colors">
              Início
            </Link>
            <i className="ri-arrow-right-s-line text-neutral-400 mx-2"></i>
            <Link to="/funcionalidades" className="text-neutral-500 hover:text-primary transition-colors">
              Funcionalidades
            </Link>
            <i className="ri-arrow-right-s-line text-neutral-400 mx-2"></i>
            <span className="text-neutral-900 font-medium">{feature.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary via-primary-700 to-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
                <i className={`${feature.icon} text-secondary mr-2`}></i>
                <span className="text-sm font-medium">{feature.subtitle}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {feature.title}
              </h1>

              <p className="text-xl text-neutral-100 mb-8 leading-relaxed">
                {feature.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-8 py-4 bg-secondary text-white text-lg font-semibold rounded-lg hover:bg-secondary-600 transition-all shadow-hover whitespace-nowrap cursor-pointer"
                >
                  Agende uma Demonstração
                </button>
                <a
                  href="https://wa.me/5511999999999?text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20o%20módulo%20de%20${encodeURIComponent(feature.title)}"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap cursor-pointer text-center"
                >
                  Falar com Especialista
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={feature.heroImage}
                  alt={feature.title}
                  className="w-full h-80 lg:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {feature.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-neutral-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Principais Benefícios
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Veja como o módulo de {feature.title} pode transformar sua operação
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {feature.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-card hover:shadow-hover transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-xl mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-primary`}></i>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{benefit.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features List Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                Recursos Completos
              </h2>
              <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
                Tudo que você precisa para gerenciar {feature.title.toLowerCase()} de forma eficiente e profissional.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {feature.features.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="flex items-start"
                  >
                    <div className="w-6 h-6 flex items-center justify-center bg-secondary/10 rounded-full mr-3 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-secondary text-sm"></i>
                    </div>
                    <span className="text-neutral-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-neutral-100 rounded-2xl p-4">
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={feature.screenshots[activeScreenshot].image}
                    alt={feature.screenshots[activeScreenshot].caption}
                    className="w-full h-80 object-cover object-top"
                  />
                </div>
                <p className="text-center text-sm text-neutral-600 mt-4">
                  {feature.screenshots[activeScreenshot].caption}
                </p>
              </div>

              {feature.screenshots.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {feature.screenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveScreenshot(index)}
                      className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                        activeScreenshot === index
                          ? 'bg-primary w-8'
                          : 'bg-neutral-300 hover:bg-neutral-400'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-lg text-neutral-600">
              Tire suas dúvidas sobre o módulo de {feature.title}
            </p>
          </div>

          <div className="space-y-4">
            {feature.faq.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-neutral-50 transition-colors"
                >
                  <span className="font-semibold text-neutral-900 pr-4">{item.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-neutral-500 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 text-neutral-600 leading-relaxed">
                    {item.answer}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center text-primary font-semibold hover:text-primary-600 transition-colors"
            >
              Ver todas as perguntas frequentes
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Features */}
      {relatedItems.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Funcionalidades Relacionadas
              </h2>
              <p className="text-lg text-neutral-600">
                Conheça outros módulos que complementam {feature.title}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedItems.map((item, index) => (
                <motion.div
                  key={item.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/funcionalidades/${item.slug}`}
                    className="group block bg-neutral-50 p-8 rounded-2xl hover:bg-white hover:shadow-hover transition-all border border-neutral-100"
                  >
                    <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl mb-6 group-hover:bg-primary/20 transition-colors">
                      <i className={`${item.icon} text-3xl text-primary`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                    <div className="mt-4 flex items-center text-primary font-semibold text-sm group-hover:translate-x-2 transition-transform">
                      Saiba mais
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-secondary via-secondary-500 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Experimentar?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Teste o módulo de {feature.title} gratuitamente por 14 dias. Sem compromisso.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-4 bg-white text-secondary text-lg font-semibold rounded-lg hover:bg-neutral-100 transition-all shadow-hover whitespace-nowrap cursor-pointer"
            >
              Começar Teste Grátis
            </button>
            <Link
              to="/planos"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap"
            >
              Ver Planos e Preços
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
